/**
 * applyNavLink — shared click handler for `nav_link` artifact buttons
 * (NavLinkButton in MarkdownRenderer, ToolCallCard's admin nav_link section).
 *
 * wouter's `navigate()` only re-renders when the ROUTE changes, and NOTHING in
 * this app re-reads the query string on navigation. So the URL contract that
 * `view_in_explorer` writes (`?chemistry=…&view=graph&x=…&y=…&color=…`) has
 * exactly one reader: this function. `loadFiltersFromURL` is called from here
 * and from `initFilterStore()` — and `initFilterStore()` has no call site at
 * all, so in practice this is the only reader there is.
 *
 * HISTORY, because the earlier version of this comment asserted the opposite
 * and the assertion was the bug. It said: "Different pathname → cross-route
 * navigation → the destination page's own mount already calls
 * loadFiltersFromURL(), so we do nothing extra." No page mount calls it.
 * Cross-route handoffs therefore applied NOTHING — no filters, no view, no
 * axes, no colour — and the user landed on whatever the Explorer happened to
 * be showing, which (because those signals are persisted) usually looked
 * plausible and stale rather than empty. Reported three times as "the button
 * does nothing" (FB-7, FB-38) and once as a filter that never arrived (FB-58
 * follow-up). Same-route clicks worked, which is exactly why it looked
 * intermittent: Ion answers from the Explorer as often as from its own page.
 *
 * So: apply on BOTH branches. The "double-apply" the old comment guarded
 * against cannot happen — there is no second caller to double with.
 */

import { loadFiltersFromURL, resetAllFilters } from '../../state/filterStore.js'
import {
  applyFilterCommand,
  beginIonTransaction,
  commitIonTransaction,
} from '../../state/commandLayer.js'

// Query params that describe a FILTER (as opposed to a view/axis/colour
// preference). Mirrors the keys `loadFiltersFromURL` treats as filters and the
// ones `view_in_explorer` (backend navigate.py) can emit.
const FILTER_PARAMS = [
  'q', 'chemistry', 'cathode', 'anode', 'mfr', 'form', 'tech', 'country',
  'energy', 'power', 'capacity', 'current', 'year', 'preset',
]

/**
 * @param {string} url - target URL from a nav_link artifact; may be a
 *   relative path (`/explorer?…`) or absolute (`https://…/explorer?…`).
 * @param {(url: string) => void} navigate - wouter's `navigate` (or
 *   `setLocation`) function for the current router instance.
 */
export function applyNavLink(url, navigate) {
  if (!url) return
  const target = new URL(url, window.location.origin)
  navigate(url)

  // A handoff button names the state it delivers — Ion's own text says things
  // like "the Explorer filtered to LFP & LTO". `loadFiltersFromURL` only ADDS
  // what the URL mentions, so without clearing first the user gets the named
  // set PLUS whatever was already active: a result that is wrong while looking
  // deliberate, and unattributable because nothing on screen says the old
  // filters are still in play.
  //
  // Only when the link actually carries a filter, though. Links that just set a
  // view or axes (`?view=graph&color=…`) must NOT wipe the filters the user set
  // themselves — that would be a second surprise in the opposite direction.
  const carriesFilter = FILTER_PARAMS.some((p) => target.searchParams.has(p))
  if (carriesFilter) resetAllFilters()

  // Reads `window.location.search`, which `navigate()` has already updated
  // (wouter pushes synchronously) — so this sees the target URL, not the old one.
  loadFiltersFromURL()
}

/**
 * applyExplorerHandoff — the button under a catalogue chart Ion drew on its own
 * page. Restricts the Explorer to the cells the chart highlighted, then goes
 * there.
 *
 * Why a cell SET rather than a filter in the URL: measured on staging, the
 * Explorer has no main-chemistry filter — its Chemistry panel is a CATHODE list
 * and an ANODE list, AND-combined. "LFP or LTO" is one value from each, so as a
 * cathode filter it silently drops LTO (29 of 32 cells) and split across both
 * lists it asks for cells that are both and returns none. The ids are the only
 * faithful way to say "these 32".
 *
 * `applyFilterCommand`'s cell-set path is that primitive, and it is not new
 * machinery: it exists precisely for a named restriction the facets cannot
 * describe, it carries a label so the Explorer can explain why only 32 cells are
 * showing, and wrapping it in a transaction gives the same "Ion: … · Revert"
 * card every other Ion steering action gets.
 *
 * `merge: false` — the button names an exact set, so it must not land on top of
 * filters the user forgot about. That was a real report: the Explorer showed
 * "RESET ALL FILTERS (2)" from an earlier session while the button claimed to
 * have filtered to two chemistries.
 *
 * @param {{url: string, cellIds?: string[], setLabel?: string, label?: string}} handoff
 * @param {(url: string) => void} navigate
 */
export function applyExplorerHandoff(handoff, navigate) {
  if (!handoff?.url) return
  const ids = handoff.cellIds
  if (Array.isArray(ids) && ids.length > 0) {
    beginIonTransaction()
    applyFilterCommand({
      merge: false,
      cellIds: ids,
      cellIdsLabel: handoff.setLabel || 'Ion selection',
    })
    commitIonTransaction(handoff.setLabel || 'Ion selection')
  }
  // Navigate last: the view/axes/colour come from the URL, and doing this after
  // the set means the Explorer mounts once, already in its final state.
  applyNavLink(handoff.url, navigate)
}
